/**
 * Debug Time Limit Context
 *
 * Provides debug overrides for time tracking settings.
 * Only used when the user has the "debug" claim active.
 * Persists overrides to localStorage for convenience during development.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

// ============================================================================
// Types
// ============================================================================

export interface DebugTimeLimitOverrides {
  /** Master enable for all debug overrides */
  enabled: boolean;

  /** Custom school hours settings */
  customSchoolHoursEnabled: boolean;
  schoolHoursStart: number; // 0-23
  schoolHoursEnd: number; // 0-23
  schoolDays: number[]; // 0-6 (Sun-Sat)

  /** Custom limits settings */
  customLimitsEnabled: boolean;
  dailyLimitSeconds: number;
  weeklyLimitSeconds: number;

  /** Custom usage settings */
  customUsageEnabled: boolean;
  todayUsageSeconds: number;
  weekUsage: Record<string, number>; // date string (YYYY-MM-DD) -> seconds
}

export interface DebugTimeLimitContextType {
  /** Current override values */
  overrides: DebugTimeLimitOverrides;

  /** Update overrides (partial update) */
  updateOverrides: (updates: Partial<DebugTimeLimitOverrides>) => void;

  /** Reset all overrides to defaults */
  resetOverrides: () => void;

  /** Toggle master enable */
  toggleEnabled: () => void;

  /** Toggle a specific section */
  toggleSchoolHours: () => void;
  toggleLimits: () => void;
  toggleUsage: () => void;

  /** Update school days (add/remove a day) */
  toggleSchoolDay: (day: number) => void;

  /** Update week usage for a specific date */
  setWeekUsageForDate: (date: string, seconds: number) => void;

  /** Counter that increments when usage should be refreshed from DB */
  usageRefreshTrigger: number;

  /** Trigger a refresh of usage data from the database */
  triggerUsageRefresh: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = "debug_time_limit_overrides";

const DEFAULT_OVERRIDES: DebugTimeLimitOverrides = {
  enabled: false,

  customSchoolHoursEnabled: false,
  schoolHoursStart: 7,
  schoolHoursEnd: 15,
  schoolDays: [1, 2, 3, 4, 5], // Mon-Fri

  customLimitsEnabled: false,
  dailyLimitSeconds: 10800, // 3h
  weeklyLimitSeconds: 18000, // 5h

  customUsageEnabled: false,
  todayUsageSeconds: 0,
  weekUsage: {},
};

// ============================================================================
// Helpers
// ============================================================================

function loadFromStorage(): DebugTimeLimitOverrides {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle any missing fields
      return { ...DEFAULT_OVERRIDES, ...parsed };
    }
  } catch (error) {
    console.warn("Failed to load debug overrides from localStorage:", error);
  }
  return DEFAULT_OVERRIDES;
}

function saveToStorage(overrides: DebugTimeLimitOverrides): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch (error) {
    console.warn("Failed to save debug overrides to localStorage:", error);
  }
}

// ============================================================================
// Context
// ============================================================================

const DebugTimeLimitContext = createContext<DebugTimeLimitContextType | undefined>(undefined);

interface DebugTimeLimitProviderProps {
  children: React.ReactNode;
}

export const DebugTimeLimitProvider: React.FC<DebugTimeLimitProviderProps> = ({ children }) => {
  const [overrides, setOverrides] = useState<DebugTimeLimitOverrides>(() => loadFromStorage());
  const [usageRefreshTrigger, setUsageRefreshTrigger] = useState(0);

  // Save to localStorage whenever overrides change
  useEffect(() => {
    saveToStorage(overrides);
  }, [overrides]);

  const triggerUsageRefresh = useCallback(() => {
    setUsageRefreshTrigger((prev) => prev + 1);
  }, []);

  const updateOverrides = useCallback((updates: Partial<DebugTimeLimitOverrides>) => {
    setOverrides((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetOverrides = useCallback(() => {
    setOverrides(DEFAULT_OVERRIDES);
  }, []);

  const toggleEnabled = useCallback(() => {
    setOverrides((prev) => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  const toggleSchoolHours = useCallback(() => {
    setOverrides((prev) => ({ ...prev, customSchoolHoursEnabled: !prev.customSchoolHoursEnabled }));
  }, []);

  const toggleLimits = useCallback(() => {
    setOverrides((prev) => ({ ...prev, customLimitsEnabled: !prev.customLimitsEnabled }));
  }, []);

  const toggleUsage = useCallback(() => {
    setOverrides((prev) => ({ ...prev, customUsageEnabled: !prev.customUsageEnabled }));
  }, []);

  const toggleSchoolDay = useCallback((day: number) => {
    setOverrides((prev) => {
      const currentDays = prev.schoolDays;
      const newDays = currentDays.includes(day)
        ? currentDays.filter((d) => d !== day)
        : [...currentDays, day].sort((a, b) => a - b);
      return { ...prev, schoolDays: newDays };
    });
  }, []);

  const setWeekUsageForDate = useCallback((date: string, seconds: number) => {
    setOverrides((prev) => ({
      ...prev,
      weekUsage: { ...prev.weekUsage, [date]: seconds },
    }));
  }, []);

  const value = useMemo<DebugTimeLimitContextType>(
    () => ({
      overrides,
      updateOverrides,
      resetOverrides,
      toggleEnabled,
      toggleSchoolHours,
      toggleLimits,
      toggleUsage,
      toggleSchoolDay,
      setWeekUsageForDate,
      usageRefreshTrigger,
      triggerUsageRefresh,
    }),
    [
      overrides,
      updateOverrides,
      resetOverrides,
      toggleEnabled,
      toggleSchoolHours,
      toggleLimits,
      toggleUsage,
      toggleSchoolDay,
      setWeekUsageForDate,
      usageRefreshTrigger,
      triggerUsageRefresh,
    ]
  );

  return <DebugTimeLimitContext.Provider value={value}>{children}</DebugTimeLimitContext.Provider>;
};

/**
 * Hook to access debug time limit overrides
 *
 * @example
 * ```tsx
 * const { overrides, updateOverrides } = useDebugTimeLimit();
 *
 * // Check if debug overrides are active
 * if (overrides.enabled && overrides.customLimitsEnabled) {
 *   // Use overrides.dailyLimitSeconds instead of default
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useDebugTimeLimit = (): DebugTimeLimitContextType => {
  const context = useContext(DebugTimeLimitContext);
  if (context === undefined) {
    throw new Error("useDebugTimeLimit must be used within a DebugTimeLimitProvider");
  }
  return context;
};

/**
 * Hook that returns just the override values (or null if debug is disabled)
 * Useful for conditionally applying overrides in other hooks
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useDebugOverrides = (): DebugTimeLimitOverrides | null => {
  const context = useContext(DebugTimeLimitContext);

  // If no provider, return null (debug not available)
  if (context === undefined) {
    return null;
  }

  // If debug not enabled, return null
  if (!context.overrides.enabled) {
    return null;
  }

  return context.overrides;
};

export default DebugTimeLimitContext;
